/**
 * Matching Engine for File Matcher Module
 */

"use strict";

import { debugMode, SEARCH_LIMIT } from './constants.js';

// ---------------------------------------------------------------------------
// Compiled-regex cache.
// Hot path: myEachSinglePass scans 30k+ files. Without caching every file ×
// every leaf pattern × every level constructs a new RegExp (~360k compiles per
// search with 10 patterns). Bounded LRU-style Map keeps memory in check while
// reusing compiled patterns across keystrokes.
// IMPORTANT: cache only regexes that are used with .test() (no `g` flag — no
// shared lastIndex state). Engine code strips `g` before compiling, so all
// patterns reaching this helper are safe.
// ---------------------------------------------------------------------------
const _REGEX_CACHE_MAX = 5000;
const _regexCache = new Map();

function getCachedRegex(pattern, flags) {
    const key = pattern + ' ' + flags;
    if (_regexCache.has(key)) return _regexCache.get(key);
    let rx = null;
    try {
        rx = new RegExp("(" + pattern + ")", flags);
    } catch (e) {
        if (debugMode === '1') console.warn('[myEach] Invalid regex skipped:', { pattern, flags, error: String(e) });
    }
    if (_regexCache.size >= _REGEX_CACHE_MAX) {
        // Evict oldest entry (Map iteration order = insertion order — naive LRU).
        const firstKey = _regexCache.keys().next().value;
        if (firstKey !== undefined) _regexCache.delete(firstKey);
    }
    _regexCache.set(key, rx);
    return rx;
}

function normalizeFlags(flags) {
    let f = flags && flags.includes('g') ? flags.replace('g', '') : (flags || '');
    if (!f.includes('u')) f += 'u';
    return f;
}

/**
 * Helper: Recursively check a condition against a file
 * @returns {Object} { matched: boolean, score: number }
 */
export function checkCondition(cond, fileObj, vStr, fullMode, isCat) {
    if (!cond || !cond.value) return { matched: true, score: 0 };
    
    let matchCount = 0;
    let totalScore = 0;
    let maxBoost = 0;
    const subValues = cond.value || [];

    // Special case: NOT condition
    if (cond.condition === "not") {
        let exclusionMatched = false;
        for (const sub of subValues) {
            if (checkCondition(sub, fileObj, vStr, fullMode, isCat).matched) {
                exclusionMatched = true;
                break;
            }
        }
        return { matched: !exclusionMatched, score: 0 };
    }

    for (const item of subValues) {
        let itemMatched = false;
        let itemScore = 0;

        if (typeof item === 'object' && item.condition) {
            // Nested condition
            const res = checkCondition(item, fileObj, vStr, fullMode, isCat);
            itemMatched = res.matched;
            itemScore = res.score;
        } else {
            // Leaf pattern
            const pattern = (typeof item === 'object' && item.pattern) ? item.pattern : item;
            const boost = (typeof item === 'object' && item.boost) ? item.boost : 1;
            const searchInDateField = (typeof item === 'object' && item.searchInDateField === true);
            const flags = (typeof item === 'object' && item.flags !== undefined && item.flags !== null) 
                ? item.flags 
                : (cond.modifier || "i");
            
            const regexFlags = normalizeFlags(flags);
            const regex = getCachedRegex(pattern, regexFlags);
            if (regex) {
                const dateStr = fileObj.d || fileObj.date || '';

                if (searchInDateField) {
                    itemMatched = dateStr ? regex.test(dateStr) : false;
                    itemScore = boost;
                } else {
                    const normPath = String(vStr.p || '').replace(/\\/g, '/');
                    const useFull = !!(fullMode || isCat);

                    const mName = regex.test(vStr.n);
                    const mPath = useFull ? regex.test("/" + normPath + "/") : false;
                    const mDate = dateStr ? regex.test(dateStr) : false;

                    itemMatched = mName || mPath || mDate;
                    if (itemMatched) {
                        if (mName && mPath) itemScore = boost * 1.2;
                        else if (mName) itemScore = boost * 1.0;
                        else if (mDate) itemScore = boost * 0.8;
                        else if (mPath) itemScore = boost * 0.5;
                    }
                }
            }
        }

        if (itemMatched) {
            matchCount++;
            if (itemScore > maxBoost) maxBoost = itemScore;
            totalScore += itemScore;
        }
    }

    const isMatched = (cond.condition === "or" && matchCount > 0) ||
                      (cond.condition === "and" && matchCount === subValues.length) ||
                      (subValues.length === 0);
    
    return { 
        matched: isMatched, 
        score: cond.condition === "or" ? maxBoost : totalScore 
    };
}

/**
 * Search files against match conditions (single pass)
 * @param {Array} strs - Array of file objects
 * @param {Array} matchCond - Match conditions
 * @param {boolean} full - Whether to use full path matching
 * @param {boolean} colorize - Whether to colorize matches
 * @param {boolean} test - Test mode
 * @param {number|null} limit - Result limit
 * @returns {Array} Array of matched files
 */
export function myEachSinglePass(strs, matchCond, full = false, colorize = false, test = false, limit = null) {
    let ms = [];
    if (colorize) { strs = [strs]; }
    
    
    let processedCount = 0;
    let matchedCount = 0;

    if (window.$ && window.$.each) {
        window.$.each(strs, function (i, str) {
            if (!str || typeof str !== 'object') {
                if (debugMode === '1') console.warn(`[myEach] Invalid str at index ${i}:`, str);
                return true;
            }
            
            const validatedStr = {
                p: str.p || str.path || '',
                n: str.n || str.name || 'Unknown',
                s: str.s || str.size || 0
            };
            
            processedCount++;
            let matchConditionTotal = 0;
            let fileScore = 0; 
            const scoreLog = [];
            
            for (const level1 of matchCond) {
                let matchCondition1 = 0;
                
                if (level1.condition == "not") {
                    let exclusionMatched = false;
                    for (const level2 of level1.value) {
                        let matchCondition2 = 0;
                        for (const v2Item of level2["value"]) {
                            const v2 = (typeof v2Item === 'object' && v2Item.pattern) ? v2Item.pattern : v2Item;
                            const searchInDateField = (typeof v2Item === 'object' && v2Item.searchInDateField === true);
                            const flags = (typeof v2Item === 'object' && v2Item.flags !== undefined && v2Item.flags !== null) 
                                ? v2Item.flags 
                                : (level2.modifier || "i");
                            const regexFlags = normalizeFlags(flags);
                            const substrRegex = getCachedRegex(v2, regexFlags);
                            if (!substrRegex) continue;
                            if (colorize === false) {
                                let patternMatched = false;
                                if (searchInDateField) {
                                    const dateStr = str.d || str.date || '';
                                    patternMatched = dateStr ? substrRegex.test(dateStr) : false;
                                } else {
                                    const normPath = String(validatedStr.p || '').replace(/\\/g, '/');
                                    const useFull = !!(full || level1.isCategoryFull || level1.isCategory);
                                    const testString = useFull ? ("/" + normPath + "/" + validatedStr.n) : validatedStr.n;
                                    patternMatched = substrRegex.test(testString);
                                }
                                if (patternMatched) matchCondition2 += 1;
                            } else if (colorize === true) {
                                str = str.replace(substrRegex, '<b>$1</b>');
                            }
                        }
                        if ((level2.condition == "or" && matchCondition2 > 0) ||
                            (level2.condition == "and" && matchCondition2 == level2["value"].length)) {
                            exclusionMatched = true;
                            break;
                        }
                    }
                    if (!exclusionMatched) matchConditionTotal += 1;
                    continue;
                }
                
                const isOrWrapper = level1.condition === "or" && 
                    level1.value && level1.value.length > 0 &&
                    level1.value[0] && level1.value[0].condition && 
                    (level1.value[0].condition === "and" || level1.value[0].condition === "or");
                
                if (isOrWrapper) {
                    let anyNestedMatched = false;
                    let bestNestedScore = 0;
                    
                    for (const nestedCond of level1.value) {
                        let nestedCondMatch = 0;
                        let nestedScore = 0;
                        
                        for (const subcond of (nestedCond.value || [])) {
                            let subcondMatch = 0;
                            let maxBoost = 0;
                            
                            for (const patternItem of (subcond.value || [])) {
                                const pattern = (typeof patternItem === 'object' && patternItem.pattern) ? patternItem.pattern : patternItem;
                                const boost = (typeof patternItem === 'object' && patternItem.boost) ? patternItem.boost : 1;
                                const searchInDateField = (typeof patternItem === 'object' && patternItem.searchInDateField === true);
                                const flags = (typeof patternItem === 'object' && patternItem.flags !== undefined && patternItem.flags !== null) 
                                    ? patternItem.flags 
                                    : (subcond.modifier || "i");
                                
                                const regexFlags = normalizeFlags(flags);
                                const regex = getCachedRegex(pattern, regexFlags);
                                if (regex) {
                                    const dateStr = str.d || str.date || '';

                                    if (colorize === false) {
                                        let patternMatched = false;
                                        let testString = '';
                                        if (searchInDateField) {
                                            testString = dateStr || '(no date)';
                                            patternMatched = dateStr ? regex.test(dateStr) : false;
                                        } else {
                                            const normPath = String(validatedStr.p || '').replace(/\\/g, '/');
                                            const useFull = !!(full || nestedCond.isCategoryFull || nestedCond.isCategory);
                                            testString = useFull ? ("/" + normPath + "/" + validatedStr.n) : validatedStr.n;
                                            if (dateStr) testString += ' ' + dateStr;
                                            patternMatched = regex.test(testString);
                                        }

                                        if (patternMatched) {
                                            subcondMatch++;
                                            if (boost > maxBoost) maxBoost = boost;
                                            if (subcond.condition === "or") continue;
                                        }
                                    } else if (colorize === true) {
                                        str = str.replace(regex, '<b>$1</b>');
                                    }
                                }
                            }
                            
                            if ((subcond.condition === "or" && subcondMatch > 0) ||
                                (subcond.condition === "and" && subcondMatch === (subcond.value || []).length)) {
                                nestedCondMatch++;
                                nestedScore += maxBoost;
                            }
                        }
                        
                        if ((nestedCond.condition === "and" && nestedCondMatch === (nestedCond.value || []).length) ||
                            (nestedCond.condition === "or" && nestedCondMatch > 0)) {
                            anyNestedMatched = true;
                            if (nestedScore > bestNestedScore) bestNestedScore = nestedScore;
                        }
                    }
                    
                    if (anyNestedMatched) {
                        matchConditionTotal += 1;
                        fileScore += bestNestedScore;
                    }
                    continue;
                }
                
                if (!level1.value || !Array.isArray(level1.value) || level1.value.length === 0) {
                    matchConditionTotal += 1;
                    continue;
                }
                
                for (const level2 of level1.value) {
                    let matchCondition2 = 0;
                    let maxBoostForLevel2 = 0;
                    let sumBoostForLevel2 = 0;
                    
                    for (const v2Item of level2["value"]) {
                        if (typeof v2Item === 'object' && v2Item.condition) {
                            const nestedResult = checkCondition(v2Item, str, validatedStr, full, level1.isCategory);
                            if (nestedResult.matched) {
                                matchCondition2++;
                                if (nestedResult.score > maxBoostForLevel2) maxBoostForLevel2 = nestedResult.score;
                                sumBoostForLevel2 += nestedResult.score;
                            }
                            continue;
                        }

                        const v2 = (typeof v2Item === 'object' && v2Item.pattern) ? v2Item.pattern : v2Item;
                        const boost = (typeof v2Item === 'object' && v2Item.boost) ? v2Item.boost : 1;
                        const searchInDateField = (typeof v2Item === 'object' && v2Item.searchInDateField === true);
                        const flags = (typeof v2Item === 'object' && v2Item.flags !== undefined && v2Item.flags !== null) 
                            ? v2Item.flags 
                            : (level2.modifier || "i");
                        const regexFlags = normalizeFlags(flags);
                        const substrRegex = getCachedRegex(v2, regexFlags);
                        if (!substrRegex) continue;
                        if (colorize === false) {
                            const dateStr = str.d || str.date || '';
                            let patternMatched = false;
                            let effectiveBoost = boost;

                            if (searchInDateField) {
                                patternMatched = dateStr ? substrRegex.test(dateStr) : false;
                                effectiveBoost = boost;
                            } else {
                                const normPath = String(validatedStr.p || '').replace(/\\/g, '/');
                                const useFull = !!(full || level1.isCategoryFull || level1.isCategory);
                                const matchInName = substrRegex.test(validatedStr.n);
                                const matchInPath = useFull ? substrRegex.test("/" + normPath + "/") : false;
                                const matchInDate = dateStr ? substrRegex.test(dateStr) : false;
                                patternMatched = matchInName || matchInPath || matchInDate;
                                
                                if (patternMatched) {
                                    if (matchInName && matchInPath) effectiveBoost = boost * 1.2;
                                    else if (matchInName) effectiveBoost = boost * 1.0;
                                    else if (matchInDate) effectiveBoost = boost * 0.8;
                                    else if (matchInPath) effectiveBoost = boost * 0.5;
                                }
                            }
                            
                            if (patternMatched) {
                                matchCondition2 += 1;
                                if (level2.condition === "or") {
                                    if (effectiveBoost > maxBoostForLevel2) maxBoostForLevel2 = effectiveBoost;
                                } else if (level2.condition === "and") {
                                    sumBoostForLevel2 += effectiveBoost;
                                }
                            }
                        } else if (colorize === true) {
                            str = str.replace(substrRegex, '<b>$1</b>');
                        }
                    }
                    
                    if (level2.condition == "or" && matchCondition2 > 0 || 
                        (level2.condition == "and" && matchCondition2 == level2["value"].length) || 
                        (level2.condition == "not" && matchCondition2 == 0)) {
                        matchCondition1 += 1;
                        if (level2.condition === "or" && matchCondition2 > 0) fileScore += maxBoostForLevel2;
                        else if (level2.condition === "and" && matchCondition2 == level2["value"].length) fileScore += sumBoostForLevel2;
                    }
                }
                if (level1.condition == "or" && matchCondition1 > 0 || 
                    (level1.condition == "and" && matchCondition1 == level1["value"].length)) {
                    matchConditionTotal += 1;
                }
            }
            
            if (limit !== null && limit !== Infinity && ms.length >= limit) return false;
            
            if (!colorize && matchConditionTotal == matchCond.length) { 
                const resultItem = {
                    ...str,
                    path: validatedStr.p, name: validatedStr.n, size: validatedStr.s,
                    p: validatedStr.p, n: validatedStr.n, s: validatedStr.s,
                    fullMode: full, score: fileScore
                };
                ms.push(resultItem);
                matchedCount++;
            } else if (colorize) { 
                ms.push(str);
            }
            return true;
        });
    }
    
    if (ms.length > SEARCH_LIMIT) ms = ms.slice(0, SEARCH_LIMIT);
    
    
    if (limit !== null && limit !== Infinity && !colorize && ms.length > limit) ms = ms.slice(0, limit);
    
    return !colorize ? ms : ms[0];
}

